import { useCallback, useEffect, useMemo, useState } from 'react';
import type { User } from '@types/index';
import {
  calculateContestAgeCategory,
  profileGenderToContestKey,
  type ContestGenderKey,
} from '@utils/contestCatalog';
import api from '@services/api';
import { useAuthStore } from '@store/authStore';

export type ContestActingKind = 'self' | 'child';

export interface ContestActingPlayer {
  playerId: string;
  displayName: string;
  kind: ContestActingKind;
  playerIdCode?: string;
  gender?: string | null;
  dateOfBirth?: string | null;
  ageCategory?: string | null;
  missingDob?: boolean;
}

const STORAGE_PREFIX = 'encycgames.contestActingPlayer.v1';

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}:${userId}`;
}

function formatPersonName(
  firstName?: string | null,
  lastName?: string | null,
  fallback?: string,
): string {
  return [firstName, lastName].filter(Boolean).join(' ').trim() || fallback || 'Player';
}

function hasDateOfBirth(value?: string | null): boolean {
  const raw = String(value || '').trim();
  if (!raw) return false;
  const parsed = new Date(raw);
  return !Number.isNaN(parsed.getTime());
}

function ageFromProfile(dateOfBirth?: string | null): string | null {
  if (!hasDateOfBirth(dateOfBirth)) return null;
  return calculateContestAgeCategory(dateOfBirth);
}

export function isContestViewOnly(user?: User | null): boolean {
  if (!user) return false;
  return user.canEditProfile === false || user.isChildInCustody === true;
}

export function listContestActingPlayers(user?: User | null): ContestActingPlayer[] {
  if (!user) return [];
  const options: ContestActingPlayer[] = [];
  if (user.playerId) {
    const dob = user.dateOfBirth || null;
    options.push({
      playerId: String(user.playerId),
      displayName: formatPersonName(user.firstName, user.lastName, user.email || 'You'),
      kind: 'self',
      playerIdCode: user.playerIdCode,
      gender: user.gender || null,
      dateOfBirth: dob,
      ageCategory: ageFromProfile(dob),
      missingDob: !hasDateOfBirth(dob),
    });
  }
  if (isContestViewOnly(user)) return options;
  for (const child of user.children || []) {
    if (!child?.playerId) continue;
    if (child.custodyStatus && child.custodyStatus !== 'active') continue;
    const dob = child.dateOfBirth || null;
    options.push({
      playerId: String(child.playerId),
      displayName: formatPersonName(child.firstName, child.lastName, child.playerIdCode || 'Child'),
      kind: 'child',
      playerIdCode: child.playerIdCode,
      gender: child.gender || null,
      dateOfBirth: dob,
      ageCategory: ageFromProfile(dob),
      missingDob: !hasDateOfBirth(dob),
    });
  }
  return options;
}

export function actingPlayerLabel(player?: ContestActingPlayer | null): string {
  if (!player) return '';
  const age = player.missingDob ? 'Add DOB' : player.ageCategory;
  const suffix = player.kind === 'child' ? 'child' : null;
  return [player.displayName, suffix && `(${suffix})`, age && `· ${age}`].filter(Boolean).join(' ');
}

export function actingPlayerContestGender(player?: ContestActingPlayer | null): ContestGenderKey | null {
  if (!player) return null;
  return profileGenderToContestKey(player.gender, player.ageCategory);
}

function readStoredPlayerId(userId?: string): string {
  if (!userId) return '';
  try {
    return localStorage.getItem(storageKey(userId)) || '';
  } catch {
    return '';
  }
}

function writeStoredPlayerId(userId: string, playerId: string): void {
  try {
    localStorage.setItem(storageKey(userId), playerId);
  } catch {
    /* ignore */
  }
}

function persistUser(next: User) {
  useAuthStore.getState().setUser(next);
  try {
    localStorage.setItem('user', JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

export function useContestActingPlayer(user?: User | null) {
  const options = useMemo(() => listContestActingPlayers(user), [user]);
  const viewOnly = isContestViewOnly(user);
  const [selectedId, setSelectedId] = useState('');

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    void api
      .get('/players/me', { skipErrorToast: true })
      .then((res) => {
        const data = res.data?.data;
        if (cancelled || !data) return;
        const current = useAuthStore.getState().user;
        if (!current?.id) return;
        const next: User = {
          ...current,
          playerId: data.id || current.playerId,
          playerIdCode: data.playerIdCode || current.playerIdCode,
          firstName: data.firstName ?? current.firstName,
          lastName: data.lastName ?? current.lastName,
          gender: data.gender ?? current.gender,
          dateOfBirth: data.dateOfBirth ?? null,
          ageCategory: ageFromProfile(data.dateOfBirth) || null,
          children: Array.isArray(data.children) ? data.children : current.children,
          canEditProfile: data.canEditProfile ?? current.canEditProfile,
          canAddChildren: data.canAddChildren ?? current.canAddChildren,
          isChildInCustody: data.isChildInCustody ?? current.isChildInCustody,
        };
        persistUser(next);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setSelectedId('');
      return;
    }
    const stored = readStoredPlayerId(String(user.id));
    const valid = options.some((option) => option.playerId === stored);
    setSelectedId(valid ? stored : options[0]?.playerId || user.playerId || '');
  }, [user?.id, user?.playerId, options]);

  const setActingPlayerId = useCallback(
    (playerId: string) => {
      setSelectedId(playerId);
      if (user?.id) writeStoredPlayerId(String(user.id), playerId);
    },
    [user?.id],
  );

  const selected = options.find((option) => option.playerId === selectedId) || options[0] || null;
  const canSwitch = !viewOnly && options.length > 1;

  return {
    selected,
    options,
    viewOnly,
    canSwitch,
    setActingPlayerId,
  };
}
